// Định dạng dùng chung cho cả hai phía chat. Thuần hàm, không hook, không gọi
// mạng — chạy được ở cổng khách (không có phiên nhân viên) lẫn màn nhân viên.
import type { ChatMessage } from "./api";

/** Hai tin cách nhau dưới ngần này thì gộp vào một cụm (như WhatsApp). */
const GROUP_WINDOW_MS = 5 * 60 * 1000;

export type ChatRow =
  | { kind: "day"; key: string; label: string }
  | {
      kind: "message";
      key: string;
      message: ChatMessage;
      showHeader: boolean;
      showTail: boolean;
    };

function dayKey(value: string): string {
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? "khong-ro" : d.toDateString();
}

/**
 * Tên người gửi để so cụm. Tin của khách không có sender → dùng chuỗi cố định.
 * Có cả LOẠI tin (QĐ 0223): ghi chú nội bộ, dòng hoạt động, tự trả lời không
 * gộp cụm với tin thường của cùng người — mỗi loại một hình riêng.
 */
function senderKey(m: ChatMessage): string {
  return `${m.senderType}:${m.kind ?? "message"}:${m.sender?.name ?? ""}`;
}

function gapMs(a: ChatMessage, b: ChatMessage): number {
  const ta = new Date(a.createdAt).getTime();
  const tb = new Date(b.createdAt).getTime();
  // Mốc rác thì coi như cách xa vô hạn: thà tách cụm còn hơn gộp nhầm hai
  // người vào một bong bóng.
  if (Number.isNaN(ta) || Number.isNaN(tb)) return Number.POSITIVE_INFINITY;
  return Math.abs(tb - ta);
}

/**
 * Chèn mốc ngày và đánh dấu tin nào cần hiện đầu cụm (ảnh + tên + chức vụ) và
 * tin nào là cuối cụm (hiện giờ + trạng thái đã xem).
 */
export function groupChatMessages(items: ChatMessage[]): ChatRow[] {
  const rows: ChatRow[] = [];
  for (let i = 0; i < items.length; i += 1) {
    const m = items[i]!;
    const prev = i > 0 ? items[i - 1] : undefined;
    const next = i + 1 < items.length ? items[i + 1] : undefined;

    if (!prev || dayKey(prev.createdAt) !== dayKey(m.createdAt)) {
      rows.push({
        kind: "day",
        // Khoá gắn thêm id tin: hai ngày rác liền nhau đều ra "khong-ro" thì
        // React nhận hai dòng trùng khoá và dựng lại nhầm chỗ.
        key: `day-${dayKey(m.createdAt)}-${m.id}`,
        label: chatDayLabel(m.createdAt),
      });
    }

    const showHeader =
      !prev ||
      senderKey(prev) !== senderKey(m) ||
      dayKey(prev.createdAt) !== dayKey(m.createdAt) ||
      gapMs(prev, m) > GROUP_WINDOW_MS;
    const showTail =
      !next ||
      senderKey(next) !== senderKey(m) ||
      dayKey(next.createdAt) !== dayKey(m.createdAt) ||
      gapMs(m, next) > GROUP_WINDOW_MS;

    rows.push({ kind: "message", key: m.id, message: m, showHeader, showTail });
  }
  return rows;
}

export function chatDayLabel(value: string, now = new Date()): string {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  const homQua = new Date(now.getTime() - 24 * 60 * 60 * 1000);
  if (d.toDateString() === now.toDateString()) return "Hôm nay";
  if (d.toDateString() === homQua.toDateString()) return "Hôm qua";
  return new Intl.DateTimeFormat("vi-VN", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  }).format(d);
}

/**
 * Nhãn trạng thái dưới tin NHÂN VIÊN gửi. Chỉ dùng ở màn nhân viên — cổng khách
 * không bao giờ nhận customerLastReadAt (quyết định 0063 mục 4).
 *
 * Thiếu mốc = khách CHƯA mở lần nào. Ghi "Đã gửi", KHÔNG ghi "Chưa xem": hai câu
 * đó khác nhau, và "chưa xem" là khẳng định mà dữ liệu không đỡ nổi.
 */
export function seenLabel(
  sentAt: string,
  customerLastReadAt: string | null | undefined,
): string {
  if (!customerLastReadAt) return "Đã gửi";
  const seen = new Date(customerLastReadAt).getTime();
  const sent = new Date(sentAt).getTime();
  if (Number.isNaN(seen) || Number.isNaN(sent)) return "Đã gửi";
  return seen >= sent ? "Khách đã xem" : "Đã gửi";
}

export function formatChatTime(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return new Intl.DateTimeFormat("vi-VN", { hour: "2-digit", minute: "2-digit" }).format(date);
}
